import { WorkflowItem } from '@cibo/core'
import { useAuth } from '@cibo/profile'
import { DataGridPro, intuitiveFromNow, Swatch } from '@cibo/ui'
import { Stack, Typography, useTheme } from '@mui/material'
import { GridColumns, GridRenderCellParams, GridRowClassNameParams } from '@mui/x-data-grid-pro'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { fieldCountsToColor } from 'utils'
import { EnrollmentDetailsCell } from './EnrollmentDetailsCell'
import { EnrollmentListItem, EnrollmentListProps } from './EnrollmentListTypes'

const SwatchCell = (params: GridRenderCellParams<WorkflowItem>) => {
  const color = fieldCountsToColor(params.row.fieldCounts)
  const { palette } = useTheme()
  if (!!color) {
    return <Swatch color={palette[color].main} />
  }
  return null
}

export const EnrollmentList = ({
  disableSelfHighlight,
  hideProgramLogo,
  hideProgramTitle,
  ...dataGridProps
}: EnrollmentListProps) => {
  const auth = useAuth()
  const { t } = useTranslation('impact/components/EnrollmentList')

  const columns = useMemo(
    () =>
      [
        {
          field: 'workflowStatus',
          minWidth: 32,
          maxWidth: 32,
          renderCell: params => <SwatchCell {...params} />,
        },
        {
          field: 'owner',
          flex: 2,
          valueGetter: ({
            row: {
              ownedBy: { userPrimaryEmail },
            },
          }) => userPrimaryEmail,
          renderCell: ({ row: { ownedBy, programContent, programId } }) =>
            !!ownedBy ? (
              <EnrollmentDetailsCell
                ownedBy={ownedBy}
                programContent={programContent}
                programId={programId}
                hideProgramLogo={hideProgramLogo}
              />
            ) : null,
        },
        {
          field: 'status',
          flex: 1,
          align: 'right',
          renderCell: ({ row: { fieldCounts, updatedAt } }) => (
            <Stack justifyContent="center" alignItems="flex-end">
              <Typography variant="body2">{t('fields', { count: fieldCounts.total })}</Typography>
              <Typography variant="caption">{intuitiveFromNow(updatedAt)}</Typography>
            </Stack>
          ),
        },
      ] as GridColumns<EnrollmentListItem>,
    [auth.userId]
  )

  return (
    <Stack spacing={2}>
      <DataGridPro<EnrollmentListItem>
        //@ts-ignore the column model expects a POJO, but we provide a class ¯\_(ツ)_/¯
        columns={columns}
        headerHeight={0}
        rowsPerPageOptions={[10]}
        getRowClassName={
          disableSelfHighlight
            ? undefined
            : ({
                row: {
                  ownedBy: { userId },
                },
              }: GridRowClassNameParams<EnrollmentListItem>) =>
                userId === auth.userId ? 'highlight' : ''
        }
        components={{
          NoRowsOverlay: () => (
            <Stack height="100%" justifyContent="center" alignItems="center" p={2}>
              <Typography data-testid="no-rows-overlay">{t('noRows')}</Typography>
            </Stack>
          ),
          ...(dataGridProps.components || {}),
        }}
        pageSize={10}
        autoHeight
        disableSelectionOnClick
        hideBorders
        hideCellFocus
        hideFooter
        {...dataGridProps}
      />
    </Stack>
  )
}
